import React from 'react';
import {Link} from 'react-router-dom';
import Chart from 'chart.js';
import {Map, Placemark, Polyline} from 'react-yandex-maps';
import YandexMapsProvider from '../common/YandexMaps';
import DeviceReadings from './DeviceReadings';
import {configureVehicleDevice, controlVehicleDoor, createIdempotencyKey, getVehicleDevice, getVehicleTelematics} from '../../api/rental';
import {ActionButton, Actions, Badge, CardTitle, CheckboxRow, ErrorText, Field, FieldGrid, Input, State, SuccessText} from './styles';

const dateText = value => value ? new Date(value).toLocaleString('ru-RU') : 'Нет данных';
const commandStatuses = {pending: 'В очереди', in_progress: 'Ожидается ответ', success: 'Выполнена', error: 'Ошибка'};
const reasons = {
  REMOTE_CONTROL_DISABLED: 'Удалённое управление отключено', DEVICE_OFFLINE: 'Устройство не на связи',
  VEHICLE_MOVING: 'Автомобиль движется', TELEMETRY_UNAVAILABLE: 'Нет достоверных данных о последней скорости',
};

export function graphPoints(points, size = 1000) {
  // В каждой группе оставляем минимум и максимум: пик скорости не исчезает при уменьшении графика.
  const result = [];
  const step = Math.max(1, Math.ceil(points.length / size));
  for (let i = 0; i < points.length; i += step) {
    const group = points.slice(i, i + step);
    const min = group.reduce((a, b) => a.speed <= b.speed ? a : b);
    const max = group.reduce((a, b) => a.speed >= b.speed ? a : b);
    result.push(...(min === max ? [min] : [min, max].sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at))));
  }
  return result;
}

function SpeedChart({points}) {
  const canvas = React.useRef(null);
  React.useEffect(() => {
    const data = graphPoints(points);
    const chart = new Chart(canvas.current, {
      type: 'line',
      data: {datasets: [{label: 'Скорость, км/ч', data: data.map(p => ({x: new Date(p.recorded_at), y: p.speed})),
        borderColor: '#2283c5', borderWidth: 2, pointRadius: 0, fill: false, lineTension: 0}]},
      options: {responsive: true, maintainAspectRatio: false, animation: {duration: 0},
        scales: {xAxes: [{type: 'time', time: {tooltipFormat: 'DD.MM.YYYY HH:mm:ss'}}],
          yAxes: [{ticks: {beginAtZero: true}, scaleLabel: {display: true, labelString: 'км/ч'}}]}},
    });
    return () => chart.destroy();
  }, [points]);
  return <div style={{height: 220}}><canvas ref={canvas} role="img" aria-label="История скорости автомобиля" /></div>;
}

export default function VehicleTelematics({authorization, vehicleId, compact = false}) {
  const [data, setData] = React.useState(null);
  const [form, setForm] = React.useState({imei: '', pin: '', remote_control_enabled: false});
  const [day, setDay] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [unknown, setUnknown] = React.useState(null);
  const mounted = React.useRef(true);
  const initialized = React.useRef(false);
  const historyRequest = React.useRef(0);
  React.useEffect(() => () => { mounted.current = false; }, []);

  const load = React.useCallback(async (initialize = false) => {
    const request = ++historyRequest.current;
    setLoading(true);
    try {
      const from = day ? new Date(`${day}T00:00:00`) : new Date(Date.now() - 86400000);
      const to = new Date(from.getTime() + 86400000);
      const result = compact ? await getVehicleDevice(authorization, vehicleId)
        : await getVehicleTelematics(authorization, vehicleId, {from: from.toISOString(), to: to.toISOString()});
      if (!mounted.current || request !== historyRequest.current) return;
      setData(result);
      if (initialize && !initialized.current) {
        initialized.current = true;
        setForm({imei: result.device.imei || '', pin: result.device.pin || '', remote_control_enabled: result.device.remote_control_enabled});
      }
    } catch (requestError) { if (mounted.current && request === historyRequest.current) setError(requestError.message); }
    finally { if (mounted.current && request === historyRequest.current) setLoading(false); }
  }, [authorization, vehicleId, day, compact]);

  React.useEffect(() => { load(true); }, [load]);

  React.useEffect(() => {
    let inFlight = false;
    const timer = setInterval(async () => {
      if (document.hidden || inFlight) return;
      inFlight = true;
      try {
        const update = await getVehicleDevice(authorization, vehicleId);
        if (mounted.current) setData(previous => previous ? {...previous, ...update} : previous);
      } catch (requestError) {
        if (mounted.current) setData(previous => previous ? {...previous, device: {...previous.device, online: false, control_error: 'DEVICE_OFFLINE'}} : previous);
      } finally { inFlight = false; }
    }, 10000);
    return () => clearInterval(timer);
  }, [authorization, vehicleId]);

  const save = async event => {
    event.preventDefault();
    setBusy(true); setError(''); setMessage('');
    try {
      await configureVehicleDevice(authorization, vehicleId, form);
      if (!mounted.current) return;
      setMessage('Устройство сохранено. После изменения IMEI/PIN дождитесь нового подключения.');
      await load();
    } catch (requestError) { if (mounted.current) setError(requestError.message); }
    finally { if (mounted.current) setBusy(false); }
  };

  const control = async (action, previous) => {
    const command = previous || {action, key: createIdempotencyKey(`door-${action}`)};
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await controlVehicleDoor(authorization, vehicleId, command.action, command.key);
      if (!mounted.current) return;
      setUnknown(null);
      if (result.status === 'success') setMessage(command.action === 'unlock' ? 'Команда открытия выполнена.' : 'Команда закрытия выполнена.');
      else setError(result.error.message);
      await load();
    } catch (requestError) {
      if (mounted.current) {
        setUnknown(command);
        setError('Ответ не получен. Команда могла выполниться. Повторная проверка использует прежний ключ.');
      }
    } finally { if (mounted.current) setBusy(false); }
  };

  const device = data && data.device;
  const points = (data && data.points) || [];
  const current = device && device.current;
  const track = points.filter(p => p.satellites >= 3).filter((p, i, list) => i % Math.max(1, Math.ceil(list.length / 2500)) === 0 || i === list.length - 1)
    .map(p => [p.latitude, p.longitude]);
  const center = current ? [current.latitude, current.longitude] : track[0];
  return (
    <section style={{marginTop: 28}} aria-label="Устройство и телеметрия">
      <CardTitle>Устройство и телеметрия</CardTitle>
      {loading && <State>Загрузка телеметрии…</State>}
      {error && <ErrorText role="alert">{error}</ErrorText>}
      {message && <SuccessText role="status">{message}</SuccessText>}
      {device && <React.Fragment>
        <Badge success={device.online}>{device.online ? 'На связи' : 'Не на связи'}</Badge>
        <p>Последняя связь: {dateText(device.last_seen_at)}<br />
          Измерение: {dateText(current && current.recorded_at)} · Скорость: {current ? `${current.speed} км/ч` : 'неизвестна'}</p>
        {!device.online && device.disconnect_reason && <p>{device.disconnect_reason}</p>}
        <DeviceReadings readings={device.readings || {}} />
        {compact && device.imei && <p><Link to={`/rental/devices/${device.imei}`}>Устройство VEGA · {device.imei} →</Link></p>}
        {current && compact && <p>Координаты устройства: {current.latitude}, {current.longitude}</p>}
        {(!compact || !device.imei) && <form onSubmit={save}>
          <FieldGrid>
            <Field>IMEI<Input value={form.imei} pattern="[0-9]{15}" maxLength={15} onChange={e => setForm({...form, imei: e.target.value})} /></Field>
            <Field>PIN<Input value={form.pin} maxLength={4} autoComplete="off" onChange={e => setForm({...form, pin: e.target.value})} /></Field>
          </FieldGrid>
          <CheckboxRow><input type="checkbox" checked={form.remote_control_enabled} onChange={e => setForm({...form, remote_control_enabled: e.target.checked})} /> Разрешить управление дверями</CheckboxRow>
          <Actions><ActionButton type="submit" disabled={busy}>Сохранить устройство</ActionButton></Actions>
        </form>}
        {!device.global_control_enabled && <p>Общий выключатель сервиса: управление отключено.</p>}
        {device.control_error && <p>{reasons[device.control_error] || device.control_error}</p>}
        <Actions>
          <ActionButton disabled={busy || loading || !!unknown || !!device.control_error} onClick={() => control('unlock')}>Открыть автомобиль</ActionButton>
          <ActionButton disabled={busy || loading || !!unknown || !!device.control_error} onClick={() => control('lock')}>Закрыть автомобиль</ActionButton>
          <ActionButton disabled={busy || loading} onClick={() => load()}>Обновить данные</ActionButton>
        </Actions>
        {busy && <p role="status">Выполняется запрос…</p>}
        {unknown && <Actions><ActionButton disabled={busy} onClick={() => control(unknown.action, unknown)}>Проверить результат команды</ActionButton></Actions>}
        {!compact && <React.Fragment><Field>История за сутки (местное время браузера)
          <Input type="date" value={day} onChange={e => setDay(e.target.value)} />
        </Field>
        <p>{day ? 'Выбранные сутки' : 'Последние 24 часа'} · Точек: {points.length}. Время точек — время измерения устройством.</p>
        {data.truncated && <ErrorText>Показаны первые 100 000 точек; история суток отображена не полностью.</ErrorText>}
        {points.length ? <SpeedChart points={points} /> : <State>За этот период измерений нет.</State>}
        {points.length > 1000 && <p>График сохраняет минимумы и максимумы групп точек; трек отображает до 2501 точки.</p>}
        {center && <YandexMapsProvider>
          <Map key={`${vehicleId}-${day}`} defaultState={{center, zoom: 13}} width="100%" height="320px">
            {track.length > 1 && <Polyline geometry={track} options={{strokeColor: '#2283c5', strokeWidth: 3}} />}
            {current && <Placemark geometry={[current.latitude, current.longitude]} properties={{iconCaption: 'Последняя точка устройства'}} />}
          </Map>
        </YandexMapsProvider>}
        <details style={{marginTop: 16}}><summary>Последние команды ({data.commands.length})</summary>
          {data.commands.map(command => <p key={command.id}>
            {dateText(command.created_at)} · {command.input_text || (command.action === 'unlock' ? 'Открыть' : command.action === 'lock' ? 'Закрыть' : command.action)} · {commandStatuses[command.status]}
            {command.error_code && ` · ${command.error_code}`}<br /><small>{command.id}</small>
          </p>)}
        </details></React.Fragment>}
      </React.Fragment>}
    </section>
  );
}
